var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { FocusOutline } from '@inter-ikea-kompis/component-focus-outline';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { ModalHeader as SkapaModalHeader, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import SustainabilityIndexModalEnum from '../enums/SustainabilityIndexModalEnum.js';
import SustainabilityIndexCard from '../sustainability-index-card/SustainabilityIndexCard.js';
import SustainabilityIndexStyle from './SustainabilityIndexStyle.js';
/**
 * @event modalOpen Triggered when a modal should be opened.
 * @event modalClose Triggered when a modal should be closed.
 */
export default class SustainabilityIndex extends KompisElement {
    constructor() {
        super(...arguments);
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Visible modal.
        this.visibleModal = null;
        this.complianceLabelDocumentUrl = null;
        this.complianceLabelDocumentImageUrl = null;
        this.sheetElement = null;
    }
    render() {
        return html `
			<style>
				${SustainabilityIndexStyle.getStyle()}
			</style>

			${this.disableLinks ? this.getImageAsButton() : this.getImageAsLink()}
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
    }
    async connectedCallback() {
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('kompis-focus-outline', FocusOutline);
        this.customElements.define('kompis-sustainability-index-card', SustainabilityIndexCard);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal === SustainabilityIndexModalEnum.durabilityIndexCard ||
                this.visibleModal === SustainabilityIndexModalEnum.repairabilityIndexCard) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        return true;
    }
    getImageAsButton() {
        if (!this.complianceLabelDocumentImageUrl) {
            return this.getNonInterativeImage();
        }
        const imageElement = this.getImageElement();
        return html `
			<kompis-focus-outline .theme="${this.theme}">
				<div
					class="imageContainer interactive"
					tabindex="0"
					role="button"
					@keydown="${(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                this.sendModalOpen();
            }
        }}"
					@click="${(event) => {
            event.preventDefault();
            event.stopPropagation();
            this.sendModalOpen();
        }}"
				>
					${imageElement}
				</div>
			</kompis-focus-outline>
		`;
    }
    getImageAsLink() {
        if (!this.complianceLabelDocumentUrl) {
            return this.getNonInterativeImage();
        }
        const imageElement = this.getImageElement();
        return html `
			<kompis-focus-outline .theme="${this.theme}">
				<a
					href="${this.complianceLabelDocumentUrl}"
					target="_blank"
					class="imageContainer interactive"
					>${imageElement}</a
				>
			</kompis-focus-outline>
		`;
    }
    getImageElement() {
        return html `<img
			alt="${this.complianceTechnicalHeading}"
			src="${this.complianceLabelImageUrl}"
		/>`;
    }
    getNonInterativeImage() {
        return html `<div class="imageContainer">${this.getImageElement()}</div>`;
    }
    sendModalOpen() {
        this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true
        }));
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        this.sheetElement = sheetElement;
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const cardElement = this.createCard();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(cardElement);
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        sheetElement.size = 'large';
        sheetElement.fullHeight = true;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.style.zIndex = '500';
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null
                    }
                }));
            }
        });
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        return sheetElement;
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const headerSpan = document.createElement('span');
        headerSpan.slot = 'closebutton-label';
        headerSpan.innerText = this.translations.modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(headerSpan);
        return sheetHeaderElement;
    }
    createCard() {
        const cardElement = this.createInternalElement('kompis-sustainability-index-card');
        cardElement.complianceLabelDocumentImageUrl = this.complianceLabelDocumentImageUrl;
        cardElement.complianceTechnicalHeading = this.complianceTechnicalHeading;
        return cardElement;
    }
}
__decorate([
    property({ type: Object, required: true })
], SustainabilityIndex.prototype, "theme", void 0);
__decorate([
    property({ type: Object, required: true })
], SustainabilityIndex.prototype, "translations", void 0);
__decorate([
    property({ type: Boolean })
], SustainabilityIndex.prototype, "disableLinks", void 0);
__decorate([
    property({ type: String })
], SustainabilityIndex.prototype, "visibleModal", void 0);
__decorate([
    property({ type: String, required: true })
], SustainabilityIndex.prototype, "complianceLabelImageUrl", void 0);
__decorate([
    property({ type: String })
], SustainabilityIndex.prototype, "complianceLabelDocumentUrl", void 0);
__decorate([
    property({ type: String })
], SustainabilityIndex.prototype, "complianceLabelDocumentImageUrl", void 0);
__decorate([
    property({ type: String, required: true })
], SustainabilityIndex.prototype, "complianceTechnicalHeading", void 0);
//# sourceMappingURL=SustainabilityIndex.js.map